import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createTenant as createTenantRecord, ensureUserTenantMembershipMirror, getMembership, getRole, getTenant, getUserDocument, getUserMemberships, setLastActiveTenant } from '../firebase/firestore';
import { getHostnameContext } from '../utils/hostname';
import { hasPermission } from '../utils/permissions';
import { applyThemeToDocument } from '../utils/theme';

const TenantContext = createContext(null);

export function TenantProvider({ user, children }) {
  const [loading, setLoading] = useState(true);
  const [memberships, setMemberships] = useState([]);
  const [activeTenant, setActiveTenant] = useState(null);
  const [membership, setMembership] = useState(null);
  const [role, setRole] = useState(null);
  const hostnameContext = useMemo(() => getHostnameContext(window.location.hostname), []);

  const loadTenants = useCallback(async () => {
    if (!user) {
      setMemberships([]);
      setActiveTenant(null);
      setMembership(null);
      setRole(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [userDoc, userMemberships] = await Promise.all([getUserDocument(user.uid), getUserMemberships(user.uid)]);

      if (!userMemberships.length) {
        if (userDoc?.lastActiveTenantId) {
          const recoveredMembership = await getMembership(userDoc.lastActiveTenantId, user.uid);
          if (recoveredMembership?.status === 'active') {
            const recoveredTenant = await getTenant(userDoc.lastActiveTenantId);
            const recoveredRole = await getRole(userDoc.lastActiveTenantId, recoveredMembership.roleId);
            const recovered = { ...recoveredMembership, tenantId: userDoc.lastActiveTenantId, tenant: recoveredTenant };
            await ensureUserTenantMembershipMirror(user.uid, recovered);
            setMemberships([recovered]);
            setActiveTenant(recoveredTenant);
            setMembership(recoveredMembership);
            setRole(recoveredRole);
            return;
          }
        }
        setMemberships([]);
        setActiveTenant(null);
        setMembership(null);
        setRole(null);
        return;
      }

      setMemberships(userMemberships);

      const preferredTenantId = userDoc?.lastActiveTenantId;
      const selected = userMemberships.find((item) => item.tenantId === preferredTenantId) || userMemberships[0];
      const [tenant, activeMembership] = await Promise.all([
        getTenant(selected.tenantId),
        getMembership(selected.tenantId, user.uid),
      ]);
      const activeRole = await getRole(selected.tenantId, activeMembership?.roleId);
      setActiveTenant(tenant);
      setMembership(activeMembership);
      setRole(activeRole);
      await setLastActiveTenant(user.uid, selected.tenantId);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadTenants().catch((error) => {
      console.error('Unable to load tenant context', error);
      setLoading(false);
    });
  }, [loadTenants]);

  useEffect(() => {
    applyThemeToDocument(activeTenant?.branding?.primaryColor || null);
  }, [activeTenant?.branding?.primaryColor]);

  const switchTenant = useCallback(async (tenantId) => {
    if (!user || !tenantId) return;
    setLoading(true);
    try {
      const [tenant, activeMembership] = await Promise.all([getTenant(tenantId), getMembership(tenantId, user.uid)]);
      if (!activeMembership || activeMembership.status !== 'active') throw new Error('You are not a member of this organization.');
      const activeRole = await getRole(tenantId, activeMembership.roleId);
      await setLastActiveTenant(user.uid, tenantId);
      setActiveTenant(tenant);
      setMembership(activeMembership);
      setRole(activeRole);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const createTenant = useCallback(async (values) => {
    const tenant = await createTenantRecord({ ...values, user });
    const [activeMembership, activeRole] = await Promise.all([
      getMembership(tenant.id, user.uid),
      getRole(tenant.id, 'owner'),
    ]);
    const ownerMembership = { ...activeMembership, tenantId: tenant.id, tenant };
    setMemberships([ownerMembership]);
    setActiveTenant(tenant);
    setMembership(activeMembership);
    setRole(activeRole);
    return tenant;
  }, [user]);

  const can = useCallback((permission) => hasPermission(role?.permissions || [], permission), [role]);

  const value = useMemo(() => ({
    loading,
    memberships,
    activeTenant,
    activeTenantId: activeTenant?.id || null,
    membership,
    role,
    permissions: role?.permissions || [],
    can,
    createTenant,
    switchTenant,
    refreshTenant: loadTenants,
    hostnameContext,
  }), [loading, memberships, activeTenant, membership, role, can, createTenant, switchTenant, loadTenants, hostnameContext]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenantContext() {
  const context = useContext(TenantContext);
  if (!context) throw new Error('useTenantContext must be used inside TenantProvider');
  return context;
}
